import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Zap } from "lucide-react";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import { ProductCard } from "@/components/ProductCard";
import { VideoAdBanner } from "@/components/VideoAdBanner";
import { ImageAdSlider } from "@/components/ImageAdSlider";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/context/I18nContext";

const CAT_FALLBACK = "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&q=80";
const catImages = {
  foodstuffs: "https://images.unsplash.com/photo-1783013958606-889689019517?w=400&q=80",
  fashion: "https://images.unsplash.com/photo-1773858441059-d741dc584aa7?w=400&q=80",
  cosmetics: "https://images.unsplash.com/photo-1596462502278-27bfdc403348?w=400&q=80",
  "wine-alcohol": "https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?w=400&q=80",
  beverages: "https://images.unsplash.com/photo-1600271886742-f049cd451bba?w=400&q=80",
  health: "https://images.unsplash.com/photo-1505576399279-565b52d4ac71?w=400&q=80",
  handmade: "https://images.unsplash.com/photo-1610701596007-11502861dcfa?w=400&q=80",
  agriculture: "https://images.unsplash.com/photo-1500937386664-56d1dfef3854?w=400&q=80",
  electronics: "https://images.unsplash.com/photo-1498049794561-7780e7231661?w=400&q=80",
  "europe-used-items": "https://images.unsplash.com/photo-1637228393246-c38a4b3d2011?w=400&q=80",
  "food-grocery": "https://images.unsplash.com/photo-1783013958606-889689019517?w=400&q=80",
  "custom-products": "https://images.pexels.com/photos/7319172/pexels-photo-7319172.jpeg?auto=compress&cs=tinysrgb&w=400",
  "wigs-accessories": "https://images.unsplash.com/photo-1492106087820-71f1a00d2b11?w=400&q=80",
  "mens-clothing": "https://images.unsplash.com/photo-1773858441059-d741dc584aa7?w=400&q=80",
  "womens-clothing": "https://images.unsplash.com/photo-1596462502278-27bfdc403348?w=400&q=80",
  "mens-shoes": "https://images.pexels.com/photos/1027130/pexels-photo-1027130.jpeg?auto=compress&cs=tinysrgb&w=400",
  "womens-shoes": "https://images.pexels.com/photos/1027130/pexels-photo-1027130.jpeg?auto=compress&cs=tinysrgb&w=400",
  bags: "https://images.pexels.com/photos/7953286/pexels-photo-7953286.jpeg?auto=compress&cs=tinysrgb&w=400",
  "auto-spare-parts": "https://images.unsplash.com/photo-1759419281480-bacc913c9606?w=400&q=80",
  furniture: "https://images.unsplash.com/photo-1599696848652-f0ff23bc911f?w=400&q=80",
  "building-materials": "https://images.pexels.com/photos/36003985/pexels-photo-36003985.jpeg?auto=compress&cs=tinysrgb&w=400",
};

export default function Home() {
  const { t } = useI18n();
  const [products, setProducts] = useState([]);
  const [flash, setFlash] = useState([]);
  const [cats, setCats] = useState([]);
  const [content, setContent] = useState(null);
  const [promoData, setPromoData] = useState({ side: "right", promos: [] });
  const [mainIdx, setMainIdx] = useState(0);

  useEffect(() => {
    api.get("/products?featured=true").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/products?flash_sale=true").then((r) => setFlash(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
    api.get("/content/home").then((r) => setContent(r.data)).catch(() => {});
    api.get("/hero-promos").then((r) => setPromoData(r.data || { side: "right", promos: [] })).catch(() => {});
  }, []);

  const promos = promoData.promos || [];
  useEffect(() => {
    if (promos.length < 2) return;
    const id = setInterval(() => setMainIdx((i) => (i + 1) % promos.length), 4500);
    return () => clearInterval(id);
  }, [promos.length]);

  const mediaUrl = (u) => (u && u.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);
  const customized = !!content?.updated_at;
  const heroTitle = customized ? (content.hero_title || "") : t("heroTitle");
  const heroSubtitle = customized ? (content.hero_subtitle || "") : t("heroSubtitle");
  const heroCtaLink = customized && content.hero_cta_link ? content.hero_cta_link : "/products";
  const banners = content?.banners || [];

  return (
    <Layout seo={{
      jsonLd: {
        "@context": "https://schema.org", "@type": "OnlineStore", name: "Xurray Mart",
        description: "Xurray Mart connects buyers and sellers with great opportunities in used cars and a wide range of products. Buy, sell, import, and export with ease.",
        url: typeof window !== "undefined" ? window.location.origin : "",
      },
    }}>
      {/* Hero */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {promos.length > 0 && (
            <div data-testid="hero-promo-layout">
              {/* Voghion-style sponsored banner: headline + framed product row + SHOP NOW */}
              <div className="relative rounded-3xl overflow-hidden shadow-xl px-5 sm:px-10 py-6 sm:py-8"
                style={{ background: "linear-gradient(135deg,#3a2416 0%,#5c3a22 45%,#2a1810 100%)" }}
                data-testid="hero-promo-showcase">
                <div className="absolute inset-0 opacity-[0.12] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 15% 20%, #fff 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
                <span className="absolute top-3 right-3 z-10 text-[10px] font-bold uppercase tracking-[0.2em] text-white/85 bg-black/30 rounded-full px-2.5 py-1">Sponsored</span>
                <div className="relative flex flex-col lg:flex-row lg:items-center gap-5 lg:gap-8">
                  {/* Headline */}
                  <div className="lg:w-1/4 shrink-0 text-center lg:text-left">
                    {heroTitle && <h2 data-testid="hero-title" className="font-display font-black text-white text-3xl sm:text-4xl leading-[0.95] uppercase" style={{ textShadow: "2px 2px 0 rgba(0,0,0,0.3)" }}>{heroTitle}</h2>}
                    {heroSubtitle && <p className="text-amber-100/80 mt-2 text-xs sm:text-sm max-w-xs mx-auto lg:mx-0">{heroSubtitle}</p>}
                    <Link to={promos[0]?.link || heroCtaLink || "/products"} data-testid="hero-promo-shop"
                      className="hidden lg:inline-flex items-center gap-2 mt-5 bg-white text-gray-900 font-black uppercase tracking-wide text-sm px-6 py-3 rounded-xl shadow-lg hover:scale-[1.04] active:scale-95 transition-transform">
                      Shop now <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                  {/* Framed product thumbnails */}
                  <div className="flex-1 min-w-0 flex gap-3 sm:gap-4 overflow-x-auto pb-1 snap-x" data-testid="hero-promo-thumbs">
                    {promos.map((p, i) => (
                      <Link key={p.promo_id || i} to={p.link || "/products"} data-testid={`hero-promo-thumb-${i}`}
                        onMouseEnter={() => setMainIdx(i)}
                        className={`group relative shrink-0 w-32 sm:w-44 rounded-xl overflow-hidden snap-start bg-white/5 ring-1 transition-all duration-300 ${i === mainIdx % promos.length ? "ring-2 ring-amber-400 shadow-lg -translate-y-0.5" : "ring-white/15 hover:ring-white/40"}`}>
                        <div className="aspect-[3/4] overflow-hidden">
                          <img src={mediaUrl(p.image_url)} alt={p.title || "Sponsored product"} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        </div>
                        <span className="pointer-events-none absolute top-1.5 left-1.5 w-3 h-3 border-l-2 border-t-2 border-white/60" />
                        <span className="pointer-events-none absolute bottom-1.5 right-1.5 w-3 h-3 border-r-2 border-b-2 border-white/60" />
                        {(p.title || p.price) && (
                          <div className="p-2 bg-black/20">
                            {p.title && <p className="text-white text-xs font-medium truncate">{p.title}</p>}
                            {p.price && <p className="text-amber-300 text-sm font-extrabold">{p.price}</p>}
                          </div>
                        )}
                      </Link>
                    ))}
                  </div>
                  {/* Mobile SHOP NOW */}
                  <Link to={promos[0]?.link || heroCtaLink || "/products"} data-testid="hero-promo-shop-mobile"
                    className="lg:hidden inline-flex items-center justify-center gap-2 bg-white text-gray-900 font-black uppercase tracking-wide text-sm px-6 py-3 rounded-xl shadow-lg">
                    Shop now <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Admin-managed promo banners */}
      {banners.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-2">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="cms-banners">
            {banners.map((b, i) => (
              <Link key={i} to={b.link || "/products"} className="relative rounded-2xl overflow-hidden h-32 group border border-border/50">
                {b.image ? <img src={mediaUrl(b.image)} alt={b.title || "banner"} className="w-full h-full object-cover group-hover:scale-105 transition-transform" /> : <div className="w-full h-full bg-gradient-to-r from-primary to-secondary" />}
                <div className="absolute inset-0 bg-black/30 flex flex-col justify-end p-4">
                  <p className="text-white font-display font-bold text-lg leading-tight">{b.title}</p>
                  {b.subtitle && <p className="text-white/85 text-xs">{b.subtitle}</p>}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Temu-style promo + quick categories */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="rounded-2xl bg-gradient-to-r from-primary/90 to-secondary text-white px-5 py-4 flex items-center justify-between gap-3 mb-5" data-testid="creditback-banner">
          <div>
            <p className="font-display font-black text-lg leading-tight">Up to €100 credit back</p>
            <p className="text-white/85 text-xs">New shopper reward · limited time</p>
          </div>
          <Link to="/products"><Button size="sm" variant="secondary" className="rounded-full whitespace-nowrap">Shop deals</Button></Link>
        </div>
        {cats.length > 0 && (
          <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1 mb-2" data-testid="category-circles">
            {cats.map((c) => (
              <Link key={c.slug} to={`/products?category=${c.slug}`} data-testid={`cat-circle-${c.slug}`} className="shrink-0 w-16 text-center group">
                <div className="w-16 h-16 rounded-full overflow-hidden border border-border/50 mb-1 group-hover:border-primary/50 transition-all">
                  <img src={c.image || catImages[c.slug] || CAT_FALLBACK} alt={c.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform" />
                </div>
                <span className="text-[11px] font-medium leading-tight block line-clamp-1">{c.name}</span>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Image advert slider (admin-managed, left/right arrows) */}
      <ImageAdSlider />

      {/* Video advert banner (admin-managed, rotating) */}
      <VideoAdBanner />

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h2 className="font-display font-bold text-2xl sm:text-3xl mb-6">{t("categories")}</h2>
        <div className="grid grid-cols-4 md:grid-cols-8 gap-4">
          {cats.map((c) => (
            <Link key={c.slug} to={`/products?category=${c.slug}`} data-testid={`cat-${c.slug}`} className="group text-center">
              <div className="aspect-square rounded-2xl overflow-hidden mb-2 border border-border/50 group-hover:border-primary/40 transition-all">
                <img src={c.image || catImages[c.slug] || CAT_FALLBACK} alt={c.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" />
              </div>
              <span className="text-xs font-medium leading-tight block">{c.name}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* Flash sales */}
      {flash.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display font-bold text-2xl sm:text-3xl flex items-center gap-2">
              <Zap className="w-7 h-7 text-destructive fill-destructive" /><span className="text-destructive">{t("flashSales")}</span>
            </h2>
            <Link to="/products?flash=1" className="text-sm font-semibold text-primary hover:underline">{t("viewAll")} →</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 lg:gap-6">
            {flash.slice(0, 5).map((p, i) => <ProductCard key={p.product_id} product={p} index={i} />)}
          </div>
        </section>
      )}

      {/* Featured */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <h2 className="font-display font-bold text-2xl sm:text-3xl mb-6">{t("featured")}</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 lg:gap-6">
          {products.map((p, i) => <ProductCard key={p.product_id} product={p} index={i} />)}
        </div>
      </section>
    </Layout>
  );
}
